import { IClauseChecklistList, deleteClauseChecklistsBulk } from "@/api";
import { DuplicateClauseChecklistDialog } from "@/components/admin/dialogs/clause-checklists/DuplicateClauseChecklistDialog";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { DestructiveDialogProps } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog.types";

import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { ContentCopy, Delete } from "@mui/icons-material";
import { ListItemIcon, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { Upload } from "lucide-react";
import React, { useState } from "react";
import { ImportChecklistDialog } from "../dialogs/import-checklist-dialog/ImportChecklistDialog";

interface IMenuActionItem {
    /**
     * The label of the action
     */
    label: string;
    /**
     * The icon of the action
     */
    icon?: React.ReactNode;
    /**
     * Whether the action is disabled
     */
    disabled?: boolean;
    /**
     * The action to perform when the item is clicked
     */
    onClick: () => void;
}

interface IProps {
    /**
     * The selected clause checklist IDs
     */
    selectedClauseChecklistIds: number[];
    /**
     * The rows of the table
     */
    rows: IClauseChecklistList[];
}

/**
 * The toolbar actions for the clause checklists table
 */
export const ClauseChecklistTableToolbarActions = ({
    selectedClauseChecklistIds,
    rows,
}: IProps) => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();

    const [importChecklistDialogOpen, setImportChecklistDialogOpen] =
        useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [selectedChecklistToDuplicate, setSelectedChecklistToDuplicate] =
        useState<IClauseChecklistList | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const menuActionItems: IMenuActionItem[] = [
        {
            label: "Delete Selected",
            icon: <Delete fontSize="small" />,
            disabled: selectedClauseChecklistIds.length === 0,
            onClick: async () => {
                // Close the menu
                handleClose();

                const dialogProps: DestructiveDialogProps = {
                    itemIds: selectedClauseChecklistIds,
                    subject: "clause checklists",
                    bulkMutationFn: (ids) =>
                        deleteClauseChecklistsBulk(ids, queryClient),
                    onSucceeded: () => undefined,
                };
                // Open the destructive dialog
                await dialogs.open(DestructiveDialog, dialogProps);
            },
        },
        {
            label: "Duplicate Selected",
            icon: <ContentCopy fontSize="small" />,
            disabled:
                selectedClauseChecklistIds.length === 0 ||
                selectedClauseChecklistIds.length > 1,
            onClick: () => {
                // Close the menu
                handleClose();

                const checklistToDuplicate = rows.find((row) =>
                    selectedClauseChecklistIds.includes(row.id)
                );

                if (checklistToDuplicate) {
                    setSelectedChecklistToDuplicate(checklistToDuplicate);
                    setDuplicateDialogOpen(true);
                }
            },
        },
        {
            label: "Import Checklist",
            icon: <Upload style={{ width: 20, height: 20 }} />,
            onClick: () => {
                // Close the menu
                handleClose();

                setImportChecklistDialogOpen(true);
            },
        },
    ];

    return (
        <>
            <ActionButton variant="text" onClick={handleClick}>
                Actions
            </ActionButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: { minWidth: 280, mt: 1.5 },
                        elevation: 0,
                        variant: "outlined",
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {menuActionItems.map(
                    ({ onClick, disabled, icon, label }, index) => (
                        <MenuItem
                            key={index}
                            onClick={onClick}
                            disabled={disabled}
                        >
                            {icon && <ListItemIcon>{icon}</ListItemIcon>}
                            {label}
                        </MenuItem>
                    )
                )}
            </Menu>
            <ImportChecklistDialog
                open={importChecklistDialogOpen}
                onClose={() => setImportChecklistDialogOpen(false)}
            />
            {selectedChecklistToDuplicate && (
                <DuplicateClauseChecklistDialog
                    open={duplicateDialogOpen}
                    onClose={() => {
                        setDuplicateDialogOpen(false);
                        setSelectedChecklistToDuplicate(null);
                    }}
                    sourceChecklist={selectedChecklistToDuplicate}
                />
            )}
        </>
    );
};
